import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import {
  useMachemetryTicks,
  useTicksRanges,
} from "components/DataGetter/DataDumpRowsGetterForSingleTimeline";
import { skuSegmentColor } from "constants/colors";
import { useSelector } from "store/useSelector";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import { StyledForeignObject } from "./BelowTimelineSegment";
import {
  getTimelineOffsetY,
  loadingSubtimelineBackgroundColor,
} from "./skuOperatorTimelineUtils";
import { safariOffset } from "./safariOffset";
import { useRef, useEffect } from "react";
import { css } from "styled-components/macro";
import { SpeedGainLossCanvasSegments } from "./SpeedGainLossCanvasSegments";
import { subTimelineLoadingDashColor } from "./skuOperatorTimelineUtils";

import { useIsRetrotagPopperOpen } from "utils/customHooks/retrotagSliceSelectors";

const SEGMENT_MARGIN = 2;
interface TicksSegmentsProps {
  width: number;
  title: string;
  tickType: "grossCount_0" | "rejectCount_0" | "netCount_0";
  order?: number;
}

function TicksSegments({ width, title, tickType, order }: TicksSegmentsProps) {
  const [ticksRanges] = useTicksRanges(); // Add this to access query range information
  const [isTimelineExpanded] = useIsTimelineExpanded();
  const y = getTimelineOffsetY(order ?? 0);
  const { xLeft, xRight, scaleTime } = useTimeAxisContext();
  const visibleRange = useSelector(s => s.legacy.visibleRange);
  const x0 = visibleRange?.ge ? scaleTime(visibleRange.ge) || 0 : xLeft;
  const x1 = visibleRange?.lt
    ? scaleTime(visibleRange.lt) || 0
    : Math.min(scaleTime(Date.now()), xRight);
  const rectLeft = Math.max(x0, xLeft);
  const rectRight = Math.min(x1, xRight);
  const rectWidth = rectRight - rectLeft;

  return (
    <StyledForeignObject
      $isHighlighted={false}
      x={rectLeft + SEGMENT_MARGIN / 2}
      y={y + safariOffset}
      height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
      width={Math.max(0, rectWidth - SEGMENT_MARGIN / 2)}
      data-server-names={[]}
      $customCss={css`
        pointer-events: none;
      `}
      className={`ticks-segments ${title}`}
    >
      <TicksCanvas
        width={width}
        tickType={tickType}
        isTimelineExpanded={isTimelineExpanded}
        ticksRanges={ticksRanges} // Pass the ticksRanges to TicksCanvas
      />
    </StyledForeignObject>
  );
}

interface TicksCanvasProps {
  width: number;
  tickType: "grossCount_0" | "rejectCount_0" | "netCount_0";
  isTimelineExpanded: boolean;
  ticksRanges: {
    processId: string | null;
    ranges: { ge: number; lt: number }[];
  };
}

// Helper function to draw loading indicator pattern
const drawLoadingIndicator = (
  ctx: CanvasRenderingContext2D,
  x: number,
  width: number,
  height: number
) => {
  const middleY = height / 2;
  const dashColor = subTimelineLoadingDashColor;

  // Save current context state
  ctx.save();

  // Draw a dashed line across the middle of the area
  ctx.strokeStyle = dashColor;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]); // Dashed line pattern

  // Draw the horizontal dashed line
  ctx.beginPath();
  ctx.moveTo(x, middleY);
  ctx.lineTo(x + width, middleY);
  ctx.stroke();

  // Reset line dash and restore context
  ctx.setLineDash([]);
  ctx.restore();
};

const TicksCanvas: React.FC<TicksCanvasProps> = ({
  width,
  tickType,
  isTimelineExpanded,
  ticksRanges,
}) => {
  const [machemetryTicks] = useMachemetryTicks();

  const { scaleTime } = useTimeAxisContext();
  const isRetrotagPopperOpen = useIsRetrotagPopperOpen(null);
  const visibleRange = useSelector(s => s.legacy.visibleRange);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    canvas.width = Math.max(0, width || 0);
    canvas.height = TAGHIGHLIGHT_BACKDROP_HEIGHT;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw backdrop on canvas
    ctx.fillStyle = loadingSubtimelineBackgroundColor;
    ctx.fillRect(0, 0, canvas.width, TAGHIGHLIGHT_BACKDROP_HEIGHT);

    // Don't show timeline when timeline is not expanded or if retrotag popper is open
    if (!isTimelineExpanded || isRetrotagPopperOpen) {
      return;
    }

    // Extract specific tick type data
    const tickData = machemetryTicks?.[tickType] || [];

    // Get query ranges to determine which areas are being loaded or have been queried
    const queryRanges = ticksRanges?.ranges || [];

    // If no query ranges, show loading indicator across the entire canvas
    if (queryRanges.length === 0) {
      drawLoadingIndicator(ctx, 0, canvas.width, TAGHIGHLIGHT_BACKDROP_HEIGHT);
      return;
    }

    // Define areas that haven't been queried yet as loading areas
    const loadingAreas: { start: number; end: number }[] = [];

    if (visibleRange?.ge && visibleRange?.lt) {
      // Convert visible range to seconds to match query range format
      const visibleRangeStart = visibleRange.ge / 1000;
      const visibleRangeEnd = visibleRange.lt / 1000;

      // Find areas that haven't been queried
      let currentPosition = visibleRangeStart;

      // Sort queryRanges by start time
      const sortedRanges = [...queryRanges].sort((a, b) => a.ge - b.ge);

      // For each sorted range, check if there's a gap before it
      for (let i = 0; i < sortedRanges.length; i++) {
        const range = sortedRanges[i];

        // If there's a gap between current position and this range, it's a loading area
        if (currentPosition < range.ge) {
          loadingAreas.push({
            start: scaleTime(currentPosition * 1000), // Convert to ms for scaleTime
            end: scaleTime(range.ge * 1000),
          });
        }

        // Update current position to end of this range if it extends further
        if (range.lt > currentPosition) {
          currentPosition = range.lt;
        }
      }

      // Check if there's a loading area after the last range
      if (currentPosition < visibleRangeEnd) {
        loadingAreas.push({
          start: scaleTime(currentPosition * 1000),
          end: scaleTime(visibleRangeEnd * 1000),
        });
      }
    }

    // Draw the specific tick type data points
    if (tickData.length > 0) {
      // Draw ticks
      ctx.fillStyle = skuSegmentColor;
      tickData.forEach(tick => {
        const tickX = scaleTime(tick.ts * 1000); // Convert tick timestamp from seconds to ms
        if (tickX >= 0 && tickX <= canvas.width) {
          const tickWidth = 1; // Width of tick mark
          const tickHeight = TAGHIGHLIGHT_BACKDROP_HEIGHT; // Height of tick mark
          const yCenter = canvas.height / 2;

          // Draw the tick mark
          ctx.fillRect(
            tickX - tickWidth / 2,
            yCenter - tickHeight / 2,
            tickWidth,
            tickHeight
          );
        }
      });
    }

    // Draw loading indicators in areas that haven't been queried yet
    loadingAreas.forEach(area => {
      // Only draw if area is valid and has positive width
      if (
        area.end > area.start &&
        area.start >= 0 &&
        area.end <= canvas.width
      ) {
        drawLoadingIndicator(
          ctx,
          area.start,
          area.end - area.start,
          TAGHIGHLIGHT_BACKDROP_HEIGHT
        );
      }
    });
  }, [
    width,
    tickType,
    isTimelineExpanded,
    machemetryTicks,
    ticksRanges,
    scaleTime,
    isRetrotagPopperOpen,
    visibleRange,
  ]);

  return (
    <canvas
      ref={canvasRef}
      id={`ticks-canvas-${tickType}`}
      data-tick-type={tickType}
      className="ticks-canvas"
      width={width}
      height={TAGHIGHLIGHT_BACKDROP_HEIGHT}
      style={{
        width: `${width}px`,
        height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
        cursor: "pointer",
        opacity: isTimelineExpanded ? 1 : 0,
        transition: "opacity 0.5s ease-in-out",
        pointerEvents: isTimelineExpanded ? "auto" : "none",
      }}
    />
  );
};

export function GrossTicksSegments({
  width,
  order,
}: {
  width: number;
  order: number;
}) {
  return (
    <TicksSegments
      width={width}
      title="Gross Ticks"
      tickType="grossCount_0"
      order={order}
    />
  );
}

export function RejectTicksSegments({
  width,
  order,
}: {
  width: number;
  order: number;
}) {
  return (
    <TicksSegments
      width={width}
      title="Reject Ticks"
      tickType="rejectCount_0"
      order={order}
    />
  );
}

export function NetTicksSegments({
  width,
  order,
}: {
  width: number;
  order: number;
}) {
  return (
    <TicksSegments
      width={width}
      title="Net Ticks"
      tickType="netCount_0"
      order={order}
    />
  );
}

export function SpeedGainLossTicksSegments({
  width,
  order,
  processId,
}: {
  width: number;
  order: number;
  processId: string;
}) {
  const isRetrotagPopperOpen = useIsRetrotagPopperOpen(processId);
  if (isRetrotagPopperOpen) {
    return null;
  }
  return (
    <SpeedGainLossCanvasSegments
      width={width}
      order={order}
      processId={processId}
    />
  );
}
